import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, ArrowRight, Phone } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { MarkdownContent } from '../components/MarkdownContent';
import { QuoteForm } from '../components/QuoteForm';
import { FloatingContact } from '../components/FloatingContact';
import { getArea, getCompanyInfo, getServices } from '../api/api';
import { useSeo } from '../hooks/useSeo';

const SITE = 'https://www.nettoplus.be';

export const AreaPage = () => {
  const { slug } = useParams();
  const [zone, setZone] = useState(null);
  const [company, setCompany] = useState(null);
  const [services, setServices] = useState([]);
  const [etat, setEtat] = useState('chargement');

  useEffect(() => {
    let actif = true;
    setEtat('chargement');
    window.scrollTo(0, 0);

    Promise.all([getArea(slug), getCompanyInfo(), getServices()])
      .then(([z, c, s]) => {
        if (!actif) return;
        setZone(z);
        setCompany(c);
        setServices(s);
        setEtat('ok');
      })
      .catch(() => actif && setEtat('erreur'));

    return () => {
      actif = false;
    };
  }, [slug]);

  // SEO local : titre/description par ville + données structurées LocalBusiness
  // (zone desservie) et fil d'Ariane, pour le référencement géographique.
  const canonical = `${SITE}/zones/${slug}`;
  const ldGraph = zone
    ? {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'LocalBusiness',
            name: company?.name || 'NETTO+',
            description:
              zone.intro || `Services de nettoyage professionnel à ${zone.name}. Devis gratuit sous 24h.`,
            ...(company?.phone ? { telephone: company.phone } : {}),
            ...(company?.address ? { address: company.address } : {}),
            areaServed: {
              '@type': 'City',
              name: zone.name,
              ...(zone.postal_code ? { postalCode: zone.postal_code } : {}),
            },
            url: canonical,
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Accueil', item: `${SITE}/` },
              { '@type': 'ListItem', position: 2, name: zone.name, item: canonical },
            ],
          },
        ],
      }
    : undefined;
  useSeo({
    title: zone && company ? `Entreprise de nettoyage à ${zone.name} | ${company.name}` : undefined,
    description: zone
      ? (zone.intro || `Services de nettoyage professionnel à ${zone.name}. Devis gratuit sous 24h.`)
      : undefined,
    path: `/zones/${slug}`,
    jsonLd: ldGraph,
  });

  if (etat === 'chargement') {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 max-w-4xl mx-auto px-4 pt-32 pb-20 w-full animate-pulse space-y-4">
          <div className="h-10 bg-gray-200 rounded w-2/3" />
          <div className="h-40 bg-gray-100 rounded" />
        </div>
        <Footer />
      </div>
    );
  }

  if (etat === 'erreur' || !zone) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center px-4 pt-32 pb-20 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Zone introuvable</h1>
          <Link to="/" className="text-blue-600 underline">
            Retour à l'accueil
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-1">
        <nav aria-label="Fil d'Ariane" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28">
          <ol className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
            <li>
              <Link to="/" className="hover:text-blue-600">
                Accueil
              </Link>
            </li>
            <li aria-hidden="true">/</li>
            <li className="text-gray-900 font-medium">{zone.name}</li>
          </ol>
        </nav>

        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
            <MapPin className="w-4 h-4" aria-hidden="true" />
            {zone.name}
            {zone.postal_code && ` · ${zone.postal_code}`}
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight">
            Entreprise de nettoyage à {zone.name}
          </h1>
          <p className="mt-4 text-lg text-gray-600 max-w-2xl leading-relaxed">
            {zone.intro ||
              `${company?.name || 'NETTO+'} intervient à ${zone.name} pour les particuliers et les entreprises. Devis gratuit, réponse sous 24 heures.`}
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="#devis"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
            >
              Demander un devis
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </a>
            {company?.phone && (
              <a
                href={`tel:${company.phone}`}
                className="inline-flex items-center gap-2 border-2 border-gray-200 hover:border-blue-600 px-6 py-3 rounded-lg font-semibold text-gray-900 transition-colors"
              >
                <Phone className="w-4 h-4" aria-hidden="true" />
                {company.phone}
              </a>
            )}
          </div>
        </section>

        {zone.content && (
          <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <MarkdownContent content={zone.content} />
          </section>
        )}

        {services.length > 0 && (
          <section className="bg-gray-50 py-14">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Nos services à {zone.name}
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {services.map((service) => (
                  <Link
                    key={service.id}
                    to={service.slug ? `/services/${service.slug}` : '/'}
                    className="group bg-white border border-gray-200 hover:border-blue-600 rounded-xl p-5 transition-colors"
                  >
                    <h3 className="font-semibold text-gray-900 group-hover:text-blue-600">
                      {service.title}
                    </h3>
                    <p className="mt-2 text-sm text-gray-600 line-clamp-3">{service.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <div id="devis">
          <QuoteForm />
        </div>
      </main>

      <Footer />
      <FloatingContact />
    </div>
  );
};

export default AreaPage;
